import {Avatar, Tooltip} from 'platform/components';
import {Box, Center, Show, ThemeColorPath} from 'platform/foundation';
import styled from 'styled-components';

import i18n from '@dms/i18n';
import {EMPTY_PLACEHOLDER, useFocusControl, useUserFullName} from '@dms/shared';

import {suffixTestId, TestIdProps} from 'shared/web';

import {useIsMobileNavigation} from '../hooks/useIsMobileNavigation';
import {AvailabilityStatus} from '../types/AvailabilityStatus';
import {getAvailabilityStatusColor} from '../utils/getAvailabilityStatusColor';
import {NavigationButton} from './NavigationButton';

interface MyProfileAndSettingsButtonProps extends TestIdProps {
  isOpen: boolean;
  status: AvailabilityStatus;
  userAvatarUrl?: string;
}

interface AvatarWithAwayIndicatorProps extends TestIdProps {
  status: AvailabilityStatus;
  color: ThemeColorPath;
  name?: string | null;
  userAvatarUrl?: string;
}

function AvatarWithAwayIndicator(props: AvatarWithAwayIndicatorProps) {
  return (
    <AvatarWrapper>
      <Avatar
        name={props.name}
        size="xSmall"
        variant="circle"
        color={props.color}
        imageUrl={props.userAvatarUrl}
      />
      <Show when={props.status !== 'available'}>
        <IndicatorWrapper
          title={i18n.t('user.awayStatus.awayBadge')}
          data-testid={suffixTestId('availabilityIndicator', props)}
        >
          <Box
            width={2}
            height={2}
            borderRadius="circular"
            border="1px solid"
            borderColor="general.white"
            backgroundColor={getAvailabilityStatusColor(props.status)}
          />
        </IndicatorWrapper>
      </Show>
    </AvatarWrapper>
  );
}

function UserAvatarButtonMobile(props: MyProfileAndSettingsButtonProps) {
  const userFullName = useUserFullName();
  const {isDisabled} = useFocusControl();

  return (
    <NavigationButton
      isActive={props.isOpen}
      tabIndex={isDisabled ? -1 : 0}
      data-testid={props['data-testid']}
    >
      <AvatarWithAwayIndicator
        name={userFullName}
        color={props.isOpen ? 'palettes.neutral.40.100' : 'palettes.neutral.30.100'}
        userAvatarUrl={props.userAvatarUrl}
        status={props.status}
        data-testid={props['data-testid']}
      />
    </NavigationButton>
  );
}

interface AvatarIconControlProps extends TestIdProps {
  isOpen: boolean;
  status: AvailabilityStatus;
  userAvatarUrl?: string;
}

const UserAvatarButtonDesktop = (props: AvatarIconControlProps) => {
  const userFullName = useUserFullName();
  const {isDisabled} = useFocusControl();

  return (
    <Center width="100%" height="100%">
      <Tooltip
        label={i18n.t('entity.user.labels.myProfile')}
        placement="bottom"
        childrenWrapperDisplay="flex"
      >
        <HeaderButtonWrapper
          $isOpen={props.isOpen}
          tabIndex={isDisabled ? -1 : 0}
          data-testid={props['data-testid']}
        >
          <AvatarWithAwayIndicator
            name={userFullName ?? EMPTY_PLACEHOLDER}
            color={props.isOpen ? 'palettes.neutral.40.100' : 'palettes.neutral.30.100'}
            userAvatarUrl={props.userAvatarUrl}
            status={props.status}
            data-testid={props['data-testid']}
          />
        </HeaderButtonWrapper>
      </Tooltip>
    </Center>
  );
};

export function UserAvatarButton(props: MyProfileAndSettingsButtonProps) {
  const isMobileNavigation = useIsMobileNavigation();

  if (isMobileNavigation) {
    return <UserAvatarButtonMobile {...props} />;
  }

  return <UserAvatarButtonDesktop {...props} />;
}

interface HeaderButtonWrapperProps {
  $isOpen: boolean;
}

const HeaderButtonWrapper = styled.button<HeaderButtonWrapperProps>`
  background-color: ${({theme, $isOpen}) => $isOpen && theme.colors.palettes.neutral[30][100]};
  height: ${({theme}) => theme.getSize(6)};
  width: ${({theme}) => theme.getSize(6)};
  border-radius: 50%;
  cursor: pointer;
  transition: box-shadow 250ms ease-in-out;

  &:focus-visible {
    outline: none;
    box-shadow: 0 0 0 ${({theme}) => theme.getSize(1)}
      ${({theme}) => theme.colors.palettes.blue[40][100]};
  }

  &:hover {
    background-color: ${({theme}) => theme.colors.palettes.neutral[30][100]};
  }
`;

const AvatarWrapper = styled.div`
  position: relative;
  display: flex;
`;

const IndicatorWrapper = styled.div`
  position: absolute;
  right: -1px;
  bottom: -1px;
  display: flex;
`;
